/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// モバイルのバリエーション選択モーダルは、clickへの変換を待たずタップ座標から直接開く。
(() => {
  const triggerSelector = ".c-variant-cart-list_mobile-trigger[aria-controls]";
  const modalSelector = ".c-variant-cart-modal";
  const maximumTapMovement = 12;
  const maximumTapDuration = 800;
  let touchStart = null;

  if (!document.querySelector(triggerSelector)) {
    return;
  }

  const setTriggerExpanded = (modalId, expanded) => {
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      if (trigger.getAttribute("aria-controls") === modalId) {
        trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
      }
    });
  };

  const setModalOpen = (modal, open) => {
    if ("open" in modal) {
      modal.open = open;
    } else if (open) {
      modal.setAttribute("open", "");
    } else {
      modal.removeAttribute("open");
    }

    document.documentElement.classList.toggle("lock-all", open);
    setTriggerExpanded(modal.id, open);
  };

  const openVariantModal = (trigger) => {
    const modal = document.getElementById(trigger.getAttribute("aria-controls"));

    if (!modal || !modal.matches(modalSelector)) {
      return false;
    }

    setModalOpen(modal, true);
    return true;
  };

  const findTriggerAtPoint = (x, y) => {
    return Array.from(document.querySelectorAll(triggerSelector)).find((trigger) => {
      const rect = trigger.getBoundingClientRect();

      return rect.width > 0
        && rect.height > 0
        && x >= rect.left
        && x <= rect.right
        && y >= rect.top
        && y <= rect.bottom;
    });
  };

  const stopThemeToggle = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  document.addEventListener("touchstart", (event) => {
    if (event.touches.length !== 1) {
      touchStart = null;
      return;
    }

    const touch = event.touches[0];
    const trigger = findTriggerAtPoint(touch.clientX, touch.clientY);

    touchStart = trigger ? {
      trigger,
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    } : null;
  }, { capture: true, passive: true });

  document.addEventListener("touchend", (event) => {
    const start = touchStart;
    touchStart = null;

    if (!start || event.changedTouches.length !== 1) {
      return;
    }

    const touch = event.changedTouches[0];
    const movement = Math.hypot(touch.clientX - start.x, touch.clientY - start.y);
    const duration = Date.now() - start.time;
    const endTrigger = findTriggerAtPoint(touch.clientX, touch.clientY);

    if (movement > maximumTapMovement || duration > maximumTapDuration || endTrigger !== start.trigger) {
      return;
    }

    if (!openVariantModal(start.trigger)) {
      return;
    }

    stopThemeToggle(event);
  }, { capture: true, passive: false });

  document.addEventListener("touchcancel", () => {
    touchStart = null;
  }, { capture: true, passive: true });

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    const trigger = target.closest(triggerSelector);

    if (trigger) {
      if (!openVariantModal(trigger)) {
        return;
      }

      stopThemeToggle(event);
      return;
    }

    const modal = target.closest(modalSelector);

    if (!modal || (!target.closest('[data-action="close"]') && !target.closest(".c-variant-cart-modal__overlay"))) {
      return;
    }

    stopThemeToggle(event);
    setModalOpen(modal, false);
  }, true);

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    const modal = document.querySelector(`${modalSelector}[open]`);

    if (!modal) {
      return;
    }

    stopThemeToggle(event);
    setModalOpen(modal, false);
  }, true);
})();

// 374px以下の端末では、374px幅のデザインを端末幅に応じて自動縮小する。
(() => {
  const minViewportWidth = 374;
  let viewport = document.querySelector('meta[name="viewport"]');

  if (!viewport) {
    viewport = document.createElement("meta");
    viewport.setAttribute("name", "viewport");
    document.head.appendChild(viewport);
  }

  const switchViewport = () => {
    const viewportContent = window.outerWidth > minViewportWidth
      ? "width=device-width, initial-scale=1"
      : `width=${minViewportWidth}`;

    if (viewport.getAttribute("content") !== viewportContent) {
      viewport.setAttribute("content", viewportContent);
    }
  };

  window.addEventListener("resize", switchViewport, false);
  switchViewport();
})();

(() => {
  const openMiniCartAfterMobileMenuClose = (event) => {
    const trigger = event.target.closest("[data-mobile-menu-cart-toggle]");

    if (!trigger) {
      return;
    }

    const mobileMenu = document.getElementById("mobile-menu-drawer");
    const miniCart = document.getElementById("mini-cart");

    if (!mobileMenu || !miniCart) {
      return;
    }

    event.preventDefault();

    const openMiniCart = () => {
      miniCart.open = true;
    };

    if (!mobileMenu.open) {
      openMiniCart();
      return;
    }

    let opened = false;
    const openOnce = () => {
      if (opened) {
        return;
      }

      opened = true;
      openMiniCart();
    };

    mobileMenu.addEventListener("transitionend", (transitionEvent) => {
      if (transitionEvent.target === mobileMenu && transitionEvent.propertyName === "transform") {
        openOnce();
      }
    }, { once: true });

    mobileMenu.open = false;
    window.setTimeout(openOnce, 650);
  };

  document.addEventListener("click", openMiniCartAfterMobileMenuClose);
})();

(() => {
  const updateProductItemSoldOutState = (input) => {
    const productItem = input.closest("product-item");

    if (!productItem) {
      return;
    }

    const soldOutOverlay = productItem.querySelector(".c-product__sold-out-overlay");

    if (!soldOutOverlay) {
      return;
    }

    const isVariantAvailable = input.getAttribute("data-variant-available") === "true";
    soldOutOverlay.hidden = isVariantAvailable;
  };

  const initializeProductItemSoldOutStates = (root = document) => {
    root.querySelectorAll("product-item .product-item-meta__swatch-list .color-swatch__radio:checked[data-variant-available]").forEach(updateProductItemSoldOutState);
  };

  document.addEventListener("change", (event) => {
    const input = event.target.closest?.("product-item .product-item-meta__swatch-list .color-swatch__radio[data-variant-available]");

    if (!input) {
      return;
    }

    updateProductItemSoldOutState(input);
  });

  document.addEventListener("shopify:section:load", (event) => {
    initializeProductItemSoldOutStates(event.target);
  });

  initializeProductItemSoldOutStates();
})();

(() => {
  const wishlistMetaSelector = "[data-product-card-wishlist-meta]";
  const wishlistComponentSelector = "wishlist-button-collection";

  const getSelectedVariantId = (productItem) => {
    const selectedSwatch = productItem.querySelector(".product-item-meta__swatch-list .color-swatch__radio:checked[data-variant-id]");
    const wishlistMeta = productItem.querySelector(wishlistMetaSelector);

    return selectedSwatch?.getAttribute("data-variant-id") || wishlistMeta?.getAttribute("data-variant-id");
  };

  const syncProductItemWishlistVariant = (productItem, variantId) => {
    if (!productItem) {
      return;
    }

    const selectedVariantId = variantId || getSelectedVariantId(productItem);

    if (!selectedVariantId) {
      return;
    }

    productItem.setAttribute("data-selected-variant-id", selectedVariantId);

    productItem.querySelectorAll(wishlistComponentSelector).forEach((component) => {
      if (component.getAttribute("data-variant-id") !== selectedVariantId) {
        component.setAttribute("data-variant-id", selectedVariantId);
      }
    });
  };

  const initializeProductItemWishlistVariants = (root = document) => {
    root.querySelectorAll("product-item").forEach((productItem) => {
      syncProductItemWishlistVariant(productItem);
    });
  };

  document.addEventListener("change", (event) => {
    const input = event.target.closest?.("product-item .product-item-meta__swatch-list .color-swatch__radio[data-variant-id]");

    if (!input) {
      return;
    }

    syncProductItemWishlistVariant(input.closest("product-item"), input.getAttribute("data-variant-id"));
  });

  if ("MutationObserver" in window) {
    const observer = new MutationObserver((mutationList) => {
      mutationList.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.matches("product-item")) {
            syncProductItemWishlistVariant(node);
            return;
          }

          const productItem = node.closest?.("product-item");

          if (productItem) {
            syncProductItemWishlistVariant(productItem);
          }

          node.querySelectorAll?.("product-item").forEach(syncProductItemWishlistVariant);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeProductItemWishlistVariants(event.target);
  });

  initializeProductItemWishlistVariants();
})();

(() => {
  const cardSelector = "product-item[data-product-card-wishlist-modal]";
  const wishlistTriggerSelector = "wishlist-button-collection";

  const getEventPath = (event) => {
    if (typeof event.composedPath === "function") {
      return event.composedPath();
    }

    return [];
  };

  const findElementInPath = (event, predicate) => {
    const path = getEventPath(event);

    for (const node of path) {
      if (node instanceof Element && predicate(node)) {
        return node;
      }
    }

    return null;
  };

  const findWishlistTrigger = (event) => {
    const target = event.target instanceof Element ? event.target.closest(wishlistTriggerSelector) : null;

    if (target) {
      return target;
    }

    return findElementInPath(event, (element) => element.matches(wishlistTriggerSelector));
  };

  const findWishlistCard = (event, trigger) => {
    const card = trigger?.closest(cardSelector);

    if (card) {
      return card;
    }

    return findElementInPath(event, (element) => element.matches(cardSelector));
  };

  const openModal = (modalId) => {
    if (!modalId) {
      return false;
    }

    const modal = document.getElementById(modalId);

    if (!modal) {
      return false;
    }

    if ("open" in modal) {
      modal.open = true;
    } else {
      modal.setAttribute("open", "");
      document.documentElement.classList.add("lock-all");
    }

    return true;
  };

  document.addEventListener("click", (event) => {
    const trigger = findWishlistTrigger(event);

    if (!trigger) {
      return;
    }

    const card = findWishlistCard(event, trigger);

    if (!card) {
      return;
    }

    const modalId = card.dataset.wishlistModalId;

    if (!openModal(modalId)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }, true);
})();

(() => {
  const updateSizeGuideState = (sizeGuide) => {
    const content = sizeGuide.querySelector(".c-product-size-guide__content");

    if (!content) {
      sizeGuide.classList.add("is-empty");
      return;
    }

    const hasHeight = content.getBoundingClientRect().height > 0;
    sizeGuide.classList.toggle("is-empty", !hasHeight);
  };

  const initializeSizeGuide = (sizeGuide) => {
    if (sizeGuide.dataset.sizeGuideInitialized === "true") {
      updateSizeGuideState(sizeGuide);
      return;
    }

    sizeGuide.dataset.sizeGuideInitialized = "true";
    const content = sizeGuide.querySelector(".c-product-size-guide__content");

    const scheduleUpdate = () => {
      window.requestAnimationFrame(() => updateSizeGuideState(sizeGuide));
    };

    if (content && "ResizeObserver" in window) {
      new ResizeObserver(scheduleUpdate).observe(content);
    }

    if (content && "MutationObserver" in window) {
      new MutationObserver(scheduleUpdate).observe(content, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    scheduleUpdate();
  };

  const initializeSizeGuides = (root = document) => {
    root.querySelectorAll(".c-product-size-guide").forEach(initializeSizeGuide);
  };

  document.addEventListener("shopify:section:load", (event) => {
    initializeSizeGuides(event.target);
  });

  if ("MutationObserver" in window && document.body) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches(".c-product-size-guide")) {
            initializeSizeGuide(node);
          }

          initializeSizeGuides(node);
        });
      });
    }).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  initializeSizeGuides();
})();

(() => {
  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.('form[is="product-form"]');

    if (!form || !form.querySelector('[data-variant-coming-soon-active="true"]')) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
})();

(() => {
  const triggerSelector = "[data-bis-trigger-button][data-variant-id]";
  const triggerId = "BIS_trigger";

  const syncTrigger = (button) => {
    const variantId = button?.getAttribute("data-variant-id");

    if (!variantId) {
      return;
    }

    document.querySelectorAll(`[id="${triggerId}"]`).forEach((trigger) => {
      if (trigger !== button) {
        trigger.removeAttribute("id");
      }
    });

    button.setAttribute("id", triggerId);
    button.setAttribute("data-variant-id", variantId);
  };

  const syncTriggerFromEvent = (event) => {
    const button = event.target.closest?.(triggerSelector);

    if (button) {
      syncTrigger(button);
    }
  };

  document.addEventListener("pointerdown", syncTriggerFromEvent, true);
  document.addEventListener("click", syncTriggerFromEvent, true);
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    const button = event.target.closest?.(triggerSelector);

    if (button) {
      syncTrigger(button);
    }
  }, true);
})();

(() => {
  const updateProductPoints = (form, variant) => {
    if (!form?.id || variant?.price == null) {
      return;
    }

    const productRerender = document.querySelector(`product-rerender[observe-form="${CSS.escape(form.id)}"]`);
    const pointsValue = productRerender?.querySelector("[data-product-points-value]");

    if (!pointsValue) {
      return;
    }

    const centsPerPoint = Number(pointsValue.dataset.pointsCentsPerPoint || 10000);
    const pointsRate = Number(pointsValue.dataset.pointsRate || 1);

    if (!Number.isFinite(centsPerPoint) || centsPerPoint <= 0 || !Number.isFinite(pointsRate) || pointsRate <= 0) {
      return;
    }

    const basePoints = Math.floor(Number(variant.price) / centsPerPoint);
    pointsValue.textContent = `${basePoints * pointsRate}pt`;
  };

  document.addEventListener("variant:changed", (event) => {
    updateProductPoints(event.target, event.detail.variant);
  });
})();

(() => {
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-product-description-more]");

    if (!button) {
      return;
    }

    const clip = button.closest(".product-tabs__description-clip");
    const content = clip?.querySelector(".product-tabs__tab-item-content--description");

    if (!content) {
      return;
    }

    clip.classList.add("is-expanded");
    content.classList.add("is-expanded");
  });
})();

(() => {
  const slideSelector = "slide-show-item.slideshow__slide";

  const revealSelectedSlide = (slide) => {
    const slideshow = slide instanceof HTMLElement ? slide.closest('slide-show[transition-type="none"]') : null;

    if (!slideshow || slide.hasAttribute("hidden")) {
      return;
    }

    slide.removeAttribute("reveal-visibility");
    slide.style.visibility = "visible";

    slide.querySelectorAll("[reveal-visibility]").forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      element.removeAttribute("reveal-visibility");
      element.style.visibility = "visible";
    });
  };

  const revealSelectedSlides = (root = document) => {
    if (!(root instanceof Document) && !(root instanceof Element)) {
      return;
    }

    root.querySelectorAll(`${slideSelector}:not([hidden])`).forEach(revealSelectedSlide);
  };

  const scheduleReveal = (root = document) => {
    window.requestAnimationFrame(() => revealSelectedSlides(root));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      scheduleReveal();
    }, { once: true });
  } else {
    scheduleReveal();
  }

  document.addEventListener("shopify:section:load", (event) => {
    scheduleReveal(event.target);
  });

  if ("MutationObserver" in window) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (!(mutation.target instanceof HTMLElement) || !mutation.target.matches(slideSelector)) {
          return;
        }

        scheduleReveal(mutation.target.closest("slide-show"));
      });
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["hidden", "reveal-visibility"],
      subtree: true
    });
  }
})();

(() => {
  customElements.whenDefined("slide-show").then(() => {
    const Slideshow = customElements.get("slide-show");
    const originalSelect = Slideshow?.prototype.select;

    if (!originalSelect || originalSelect.hasCustomNoneTransition) {
      return;
    }

    const selectWithOptionalTransition = async function(index, shouldTransition = true, reverseDirection = false) {
      if (this.getAttribute("transition-type") !== "none") {
        return originalSelect.call(this, index, shouldTransition, reverseDirection);
      }

      if (this.isTransitioning) {
        return;
      }

      const newItem = this.items?.[index];
      const previousItem = this.items?.[this.selectedIndex];

      if (!newItem || newItem === previousItem) {
        return;
      }

      this.isTransitioning = true;

      try {
        newItem._preloadImages?.();

        if (typeof newItem._untilReady === "function") {
          await newItem._untilReady().catch(() => {});
        }

        newItem.removeAttribute("hidden");
        newItem.removeAttribute("reveal-visibility");
        newItem.style.visibility = "visible";

        newItem.querySelectorAll("[reveal], [reveal-visibility]").forEach((element) => {
          element.removeAttribute("reveal");
          element.removeAttribute("reveal-visibility");

          if (element instanceof HTMLElement) {
            element.style.visibility = "visible";
          }
        });

        if (previousItem) {
          previousItem.setAttribute("hidden", "");
        }

        if (this.pageDots) {
          this.pageDots.selectedIndex = newItem.index;
        }

        this.items[(newItem.index + 1) % this.items.length]?._preloadImages?.();
      } finally {
        this.isTransitioning = false;
      }
    };

    selectWithOptionalTransition.hasCustomNoneTransition = true;
    Slideshow.prototype.select = selectWithOptionalTransition;
  });
})();

(() => {
  const customVariantFormSelector = 'form[is="product-form"][id^="custom-variant-cart-list-"]';
  const propertyInputSelector = "[data-line-item-property-input][data-property-name]";
  const syncedInputSelector = "[data-synced-line-item-property]";

  const createSyncedInput = (form, propertyName, value) => {
    const input = document.createElement("input");

    input.type = "hidden";
    input.name = `properties[${propertyName}]`;
    input.value = value;
    input.setAttribute("data-synced-line-item-property", "");
    form.appendChild(input);
  };

  const getPropertyValue = (input) => {
    if (input.dataset.propertyType === "checkbox") {
      return input.checked
        ? input.dataset.checkedValue || input.value
        : input.dataset.uncheckedValue || "";
    }

    return input.value;
  };

  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.(customVariantFormSelector);

    if (!form) {
      return;
    }

    const productInfo = form.closest(".c-product-info");

    if (!productInfo) {
      return;
    }

    form.querySelectorAll(syncedInputSelector).forEach((input) => input.remove());

    for (const input of productInfo.querySelectorAll(propertyInputSelector)) {
      const propertyName = input.dataset.propertyName;
      const propertyValue = getPropertyValue(input);

      if (!propertyName) {
        continue;
      }

      if (input.dataset.propertyRequired === "true" && propertyValue.trim() === "") {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.reportValidity?.();
        input.focus?.();
        return;
      }

      if (input.dataset.propertyType !== "checkbox" && propertyValue === "") {
        continue;
      }

      createSyncedInput(form, propertyName, propertyValue);
    }
  }, true);
})();

(() => {
  const sectionSelector = "[data-custom-featured-collection]";
  const productListSelector = ".c-featured-collection_product-list";
  const endpointSectionId = "custom-featured-collection-products";
  const initializedSections = new WeakSet();
  const loadPromises = new WeakMap();
  const requestCache = new Map();
  const mobileProductPreloadMediaQuery = "(max-width: 740px)";
  const mobileProductPreloadCount = 2;
  const mobileProductPreloadTimeout = 2500;
  const mobileProductPreloadRootMargin = "1400px 0px";

  const isMobileProductViewport = () => {
    return "matchMedia" in window
      ? window.matchMedia(mobileProductPreloadMediaQuery).matches
      : window.innerWidth <= 740;
  };

  const getDirectionFlip = () => {
    return window.themeVariables?.settings?.direction === "rtl" ? -1 : 1;
  };

  const updateProgress = (productList) => {
    const list = productList.querySelector(".c-featured-collection_list");
    const scroller = productList.querySelector(".c-featured-collection_scroller");
    const progressBar = productList.querySelector(".c-featured-collection_progress-bar");
    const progressWrapper = progressBar?.closest(".c-featured-collection_progress");

    if (!list || !progressBar) {
      return;
    }

    const listMaxScroll = list.scrollWidth - list.clientWidth;
    const scrollerMaxScroll = scroller ? scroller.scrollWidth - scroller.clientWidth : 0;
    const scrollElement = listMaxScroll > 1 ? list : scrollerMaxScroll > 1 ? scroller : list;
    const maxScroll = Math.max(0, scrollElement.scrollWidth - scrollElement.clientWidth);
    const currentScroll = scrollElement.scrollLeft * getDirectionFlip();
    const progress = maxScroll > 0 ? Math.max(0, Math.min(1, currentScroll / maxScroll)) : 1;

    if (progressWrapper) {
      progressWrapper.hidden = maxScroll <= 0;
    }

    progressBar.style.setProperty("--c-featured-progress", progress);
  };

  const scheduleProgressUpdate = (productList) => {
    if (productList.dataset.progressFrame) {
      return;
    }

    productList.dataset.progressFrame = requestAnimationFrame(() => {
      delete productList.dataset.progressFrame;
      updateProgress(productList);
    });
  };

  const getSectionRequestUrl = (rawUrl) => {
    const url = new URL(rawUrl, window.location.origin);

    url.searchParams.set("section_id", endpointSectionId);
    return url.toString();
  };

  const fetchProductsPage = (requestUrl) => {
    if (!requestCache.has(requestUrl)) {
      const request = fetch(requestUrl, {
        credentials: "same-origin",
        headers: { Accept: "text/html" }
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`Product request failed with status ${response.status}.`);
        }

        return response.text();
      }).catch((error) => {
        requestCache.delete(requestUrl);
        throw error;
      });

      requestCache.set(requestUrl, request);
    }

    return requestCache.get(requestUrl);
  };

  const rewriteProductIds = (productItem, namespace) => {
    const idMap = new Map();

    productItem.querySelectorAll("[id]").forEach((element) => {
      const oldId = element.id;
      const newId = `${namespace}-${oldId}`;

      idMap.set(oldId, newId);
      element.id = newId;
    });

    ["aria-controls", "aria-labelledby", "aria-describedby", "for", "form", "list"].forEach((attribute) => {
      productItem.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const value = element.getAttribute(attribute);

        if (!value) {
          return;
        }

        const rewrittenValue = value.split(/\s+/).map((id) => idMap.get(id) || id).join(" ");
        element.setAttribute(attribute, rewrittenValue);
      });
    });

    productItem.querySelectorAll('[href^="#"]').forEach((element) => {
      const oldId = element.getAttribute("href").slice(1);

      if (idMap.has(oldId)) {
        element.setAttribute("href", `#${idMap.get(oldId)}`);
      }
    });

    const wishlistModalId = productItem.dataset.wishlistModalId;

    if (wishlistModalId && idMap.has(wishlistModalId)) {
      productItem.dataset.wishlistModalId = idMap.get(wishlistModalId);
    }

    productItem.querySelectorAll(".color-swatch__radio[name]").forEach((input) => {
      input.name = `${namespace}-${input.name}`;
    });
  };

  const prepareProductItem = (sourceItem, productList, index) => {
    const productItem = document.importNode(sourceItem, true);
    const namespace = `${productList.id}-lazy-${index + 1}`;

    rewriteProductIds(productItem, namespace);

    if (productList.dataset.enableRanking !== "true") {
      productItem.querySelectorAll(".c-product__ranking-badge").forEach((badge) => badge.remove());
    }

    if (productList.dataset.productImageSizes) {
      productItem.querySelectorAll(".product-item__primary-image, .product-item__secondary-image").forEach((image) => {
        image.setAttribute("sizes", productList.dataset.productImageSizes);
      });
    }

    return productItem;
  };

  const preloadProductImage = (image) => {
    const source = image.getAttribute("src");
    const sourceSet = image.getAttribute("srcset");

    image.setAttribute("loading", "eager");
    image.setAttribute("decoding", "async");

    if (!source && !sourceSet) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const preloader = new Image();
      const sizes = image.getAttribute("sizes");
      let settled = false;

      const completePreload = () => {
        if (settled) {
          return;
        }

        settled = true;
        preloader.onload = null;
        preloader.onerror = null;

        if (typeof preloader.decode === "function") {
          preloader.decode().catch(() => {}).finally(resolve);
          return;
        }

        resolve();
      };

      preloader.onload = completePreload;
      preloader.onerror = completePreload;
      preloader.decoding = "async";

      if (sizes) {
        preloader.sizes = sizes;
      }

      if (sourceSet) {
        preloader.srcset = sourceSet;
      }

      if (source) {
        preloader.src = source;
      }

      if (preloader.complete) {
        completePreload();
      }
    });
  };

  const preloadFirstMobileProductImages = (productItems) => {
    if (!isMobileProductViewport()) {
      return Promise.resolve();
    }

    const imageLoads = productItems.slice(0, mobileProductPreloadCount).map((productItem) => {
      const image = productItem.querySelector(".product-item__primary-image:not([hidden])");

      return image ? preloadProductImage(image) : Promise.resolve();
    });

    if (imageLoads.length === 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let settled = false;

      const complete = () => {
        if (settled) {
          return;
        }

        settled = true;
        window.clearTimeout(timeoutId);
        resolve();
      };

      const timeoutId = window.setTimeout(complete, mobileProductPreloadTimeout);

      Promise.all(imageLoads).then(complete);
    });
  };

  const updateSliderImageRatio = (productList, productItems) => {
    const prevNextButtons = productList.querySelector("prev-next-buttons");

    if (!prevNextButtons) {
      return;
    }

    const largestAspectRatio = productItems.reduce((currentRatio, productItem) => {
      const aspectRatioElement = productItem.querySelector(".product-item__aspect-ratio");
      const aspectRatio = Number.parseFloat(aspectRatioElement?.style.getPropertyValue("--aspect-ratio"));

      return Number.isFinite(aspectRatio) ? Math.max(currentRatio, aspectRatio) : currentRatio;
    }, 1);

    prevNextButtons.style.setProperty("--smallest-image-aspect-ratio", largestAspectRatio);
  };

  const loadProductList = (productList) => {
    if (!productList?.hasAttribute("data-lazy-products") || productList.dataset.lazyState === "loaded") {
      return Promise.resolve();
    }

    if (loadPromises.has(productList)) {
      return loadPromises.get(productList);
    }

    const loadPromise = (async () => {
      const errorMessage = productList.querySelector("[data-lazy-products-error]");
      const parsedTargetCount = Number.parseInt(productList.dataset.productsCount, 10);
      const targetCount = Math.min(50, Math.max(1, Number.isFinite(parsedTargetCount) ? parsedTargetCount : 12));
      const productItems = [];
      const requestedUrls = new Set();
      let requestUrl = getSectionRequestUrl(productList.dataset.collectionUrl);

      productList.dataset.lazyState = "loading";
      productList.setAttribute("aria-busy", "true");

      if (errorMessage) {
        errorMessage.hidden = true;
      }

      while (requestUrl && productItems.length < targetCount && !requestedUrls.has(requestUrl)) {
        requestedUrls.add(requestUrl);

        const responseText = await fetchProductsPage(requestUrl);
        const responseDocument = new DOMParser().parseFromString(responseText, "text/html");
        const responseRoot = responseDocument.querySelector("[data-custom-featured-products-response]");

        if (!responseRoot) {
          throw new Error("Product response markup was not found.");
        }

        const sourceItems = responseRoot.querySelectorAll("[data-custom-featured-product-card] > product-item");

        for (const sourceItem of sourceItems) {
          if (productItems.length >= targetCount) {
            break;
          }

          productItems.push(prepareProductItem(sourceItem, productList, productItems.length));
        }

        requestUrl = responseRoot.dataset.nextPageUrl
          ? getSectionRequestUrl(responseRoot.dataset.nextPageUrl)
          : "";
      }

      if (!productList.isConnected) {
        return;
      }

      await preloadFirstMobileProductImages(productItems);

      if (!productList.isConnected) {
        return;
      }

      const list = productList.querySelector(".c-featured-collection_list");

      if (!list || productItems.length === 0) {
        throw new Error("No products were returned.");
      }

      list.replaceChildren(...productItems);
      productList.productListInner = list;
      productList.productItems = productItems;
      productList._animation = null;
      productList.dataset.lazyState = "loaded";
      productList.removeAttribute("aria-busy");
      updateSliderImageRatio(productList, productItems);
      requestAnimationFrame(() => updateProgress(productList));
    })().then(() => {
      loadPromises.delete(productList);
    }).catch((error) => {
      loadPromises.delete(productList);
      productList.dataset.lazyState = "error";
      productList.removeAttribute("aria-busy");

      const errorMessage = productList.querySelector("[data-lazy-products-error]");

      if (errorMessage) {
        errorMessage.hidden = false;
      }

      console.error("Custom featured collection products could not be loaded.", error);
    });

    loadPromises.set(productList, loadPromise);
    return loadPromise;
  };

  const getSelectedProductList = (section, productLists, tabsNav) => {
    const selectedButton = tabsNav?.querySelector('button[aria-expanded="true"][aria-controls]');
    const selectedId = selectedButton?.getAttribute("aria-controls");
    const selectedList = selectedId ? document.getElementById(selectedId) : productLists.find((productList) => !productList.hidden);

    return selectedList && section.contains(selectedList) ? selectedList : null;
  };

  const initializeSection = (section) => {
    if (initializedSections.has(section)) {
      return;
    }

    initializedSections.add(section);

    const productLists = Array.from(section.querySelectorAll(productListSelector));
    const tabsNav = section.querySelector(".c-featured-collection_tabs");
    const linkButton = section.querySelector("[data-featured-collection-button]");

    tabsNav?.addEventListener("tabs-nav:changed", (event) => {
      const button = event.detail?.button;
      const buttonUrl = button?.dataset.buttonUrl;
      const productListId = button?.getAttribute("aria-controls");
      const productList = productListId ? document.getElementById(productListId) : null;

      if (linkButton && buttonUrl) {
        linkButton.href = buttonUrl;
      }

      if (productList && section.contains(productList)) {
        loadProductList(productList);
      }
    });

    section.addEventListener("click", (event) => {
      const retryButton = event.target.closest?.("[data-lazy-products-retry]");

      if (!retryButton || !section.contains(retryButton)) {
        return;
      }

      const productList = retryButton.closest(productListSelector);

      if (productList) {
        loadProductList(productList);
      }
    });

    productLists.forEach((productList) => {
      const list = productList.querySelector(".c-featured-collection_list");
      const scroller = productList.querySelector(".c-featured-collection_scroller");

      if (!list) {
        return;
      }

      list.addEventListener("scroll", () => scheduleProgressUpdate(productList), { passive: true });

      if (scroller && scroller !== list) {
        scroller.addEventListener("scroll", () => scheduleProgressUpdate(productList), { passive: true });
      }

      new MutationObserver(() => requestAnimationFrame(() => updateProgress(productList))).observe(productList, {
        attributes: true,
        attributeFilter: ["hidden"]
      });

      requestAnimationFrame(() => updateProgress(productList));
    });

    const loadSelectedProductList = () => {
      const selectedList = getSelectedProductList(section, productLists, tabsNav);

      if (selectedList) {
        loadProductList(selectedList);
      }
    };

    if (productLists.some((productList) => productList.hasAttribute("data-lazy-products"))) {
      if ("IntersectionObserver" in window) {
        const visibilityRootMargin = isMobileProductViewport() ? mobileProductPreloadRootMargin : "500px 0px";
        const visibilityObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }

          visibilityObserver.disconnect();
          loadSelectedProductList();
        }, { rootMargin: visibilityRootMargin });

        visibilityObserver.observe(section);
      } else {
        loadSelectedProductList();
      }
    }
  };

  document.querySelectorAll(sectionSelector).forEach(initializeSection);

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.matches?.(sectionSelector)) {
      initializeSection(event.target);
    }

    event.target.querySelectorAll?.(sectionSelector).forEach(initializeSection);
  });

  window.addEventListener("resize", () => {
    document.querySelectorAll(productListSelector).forEach(updateProgress);
  });
})();
